/**
 * Step 1 of sign-in: email + password.
 *
 * This screen deliberately owns no session state and no routing. A successful
 * password check does NOT mean the user is in — for any account with 2FA
 * enabled the server answers `{mfa_required: true, mfa_token}` and issues no
 * cookies at all (`app/auth/routes.py`). Treating that as a login would be the
 * whole point of the second factor, defeated. So the component reports what
 * happened through callbacks and lets its parent decide what comes next.
 */
import { useState } from 'react'
import type { FormEvent, ReactNode } from 'react'

import { ApiError, api } from '../api/client'
import type { LoginResponse } from '../api/types'
import { LanguageSwitcher, useI18n } from '../i18n'
import type { TParams, TranslationKey } from '../i18n'

/**
 * A message to show the user, kept as a key rather than a rendered string.
 *
 * Both auth screens hold their failure in state, and the string is produced at
 * render time from this, so switching language re-renders the error instead of
 * freezing whichever language it happened to fail in.
 */
export interface AuthMessage {
  key: TranslationKey
  params?: TParams
}

export interface LoginPageProps {
  /** The password was right but a TOTP code is still required. */
  onMfaRequired: (mfaToken: string) => void
  /**
   * The password was right and the account owes a second factor it has never
   * set up. Also not a session — the server sets no cookies for this answer
   * either — so it is handed up as a scoped `enrollment` token and the user
   * goes into enrollment, at this sign-in and at every one after it.
   *
   * The address goes with it: the enrollment screen is where a QR is scanned
   * and one irreplaceable set of backup codes is burned, so it has to be able
   * to say whose account that is. `/auth/login` does not echo the email back
   * (it must not confirm which addresses exist), so the value the user just
   * typed is the only source.
   */
  onEnrollmentRequired?: (enrollmentToken: string, email: string) => void
  /** The account has no 2FA: the session cookies are already set. */
  onAuthenticated?: (result: LoginResponse) => void
}

export function LoginPage({
  onMfaRequired,
  onEnrollmentRequired,
  onAuthenticated,
}: LoginPageProps) {
  const { t } = useI18n()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<AuthMessage | null>(null)
  const [pending, setPending] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending) return
    setPending(true)
    setError(null)
    try {
      const result = await api.post<LoginResponse>('/auth/login', { email, password })
      if (result.mfa_required) {
        if (!result.mfa_token) {
          // Advancing without a token would show a code box that can only ever
          // fail; surfacing it as an error is the honest outcome.
          setError({ key: 'auth.login.missingMfaToken' })
          return
        }
        onMfaRequired(result.mfa_token)
        return
      }
      if (result.enrollment_required) {
        // Same reasoning as the MFA branch, and the same refusal to guess: an
        // enrollment screen with no token can only produce a 401, and treating
        // the answer as a login would be the gate defeated.
        if (!result.enrollment_token || !onEnrollmentRequired) {
          setError({ key: 'auth.login.missingEnrollmentToken' })
          return
        }
        onEnrollmentRequired(result.enrollment_token, email)
        return
      }
      onAuthenticated?.(result)
    } catch (err) {
      setError(describeAuthError(err, 'login'))
    } finally {
      setPending(false)
    }
  }

  return (
    <AuthCard
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle')}
      onSubmit={submit}
    >
      <Field id="login-email" label={t('auth.login.email')}>
        <input
          id="login-email"
          type="email"
          name="email"
          autoComplete="username"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={INPUT}
        />
      </Field>

      <Field id="login-password" label={t('auth.login.password')}>
        <input
          id="login-password"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={INPUT}
        />
      </Field>

      <FormError message={error && t(error.key, error.params)} />

      <SubmitButton pending={pending}>{t('auth.login.submit')}</SubmitButton>
    </AuthCard>
  )
}

/* ------------------------------------------------------------------ */
/* Shared by both auth screens (imported by TwoFactorPage).            */
/* ------------------------------------------------------------------ */

export const INPUT =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg ' +
  'placeholder:text-muted disabled:opacity-60'

/**
 * The language switch, for the screens that have no `AppHeader` to put it in.
 *
 * `AppHeader` only renders once you are signed IN, so mounting the switcher
 * there alone left every signed-out screen — sign-in, 2FA, invite redemption,
 * enrollment — with no way to change language. That is precisely backwards:
 * the browser's own preference is what decides the language on a first visit,
 * so the person most likely to land in a language they cannot read is the one
 * standing at the sign-in form, and until they get past it the control they
 * need is on the other side. Right-aligned over the card, in the flow rather
 * than absolutely positioned, so it cannot land on top of the card on a short
 * viewport.
 */
export function SignedOutLanguageBar({ className = 'max-w-sm' }: { className?: string }) {
  return (
    <div className={`flex w-full justify-end ${className}`}>
      <LanguageSwitcher />
    </div>
  )
}

export function AuthCard({
  title,
  subtitle,
  onSubmit,
  children,
}: {
  title: string
  subtitle?: string
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  children: ReactNode
}) {
  return (
    <main className="flex h-full flex-col items-center justify-center gap-3 bg-bg px-4">
      <SignedOutLanguageBar />
      <form
        onSubmit={onSubmit}
        noValidate
        className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-surface p-6 shadow-sm"
      >
        <header className="space-y-1">
          <h1 className="text-lg font-semibold text-fg">{title}</h1>
          {subtitle && <p className="text-xs text-muted">{subtitle}</p>}
        </header>
        {children}
      </form>
    </main>
  )
}

export function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-fg">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  )
}

export function FormError({ message }: { message: string | null }) {
  // `role="alert"` so a screen reader announces the failure; without it a
  // keyboard user submits, hears nothing, and assumes the button is broken.
  if (!message) return null
  return (
    <p role="alert" className="rounded-md bg-down/10 px-3 py-2 text-sm text-down">
      {message}
    </p>
  )
}

export function SubmitButton({ pending, children }: { pending: boolean; children: ReactNode }) {
  return (
    <button
      type="submit"
      disabled={pending}
      aria-busy={pending}
      className="flex w-full items-center justify-center gap-2 rounded-md bg-accent px-3 py-2
                 text-sm font-medium text-white disabled:opacity-60"
    >
      {/* The label stays put while the request is in flight: a button whose
          name changes mid-action is harder to follow, for people and for tests. */}
      {pending && (
        <span
          aria-hidden="true"
          className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white"
        />
      )}
      {children}
    </button>
  )
}

/**
 * Turn an API failure into something a human can act on.
 *
 * A 401 from `/auth/login` is answered with one message for both a wrong email
 * and a wrong password. That is not vagueness for its own sake: the backend
 * goes out of its way to make the two indistinguishable — it hashes a dummy
 * password for unknown accounts so the timing matches — and a friendlier "no
 * such account" here would hand back the account enumeration it just prevented.
 * That holds in every language: `auth.error.badCredentials` is one key, and the
 * Chinese string is as exact as the English one.
 *
 * Returns a key, not a sentence, so the caller can render it in the language
 * that is current when it paints rather than the one that was current when the
 * request failed.
 */
export function describeAuthError(err: unknown, step: 'login' | 'mfa'): AuthMessage {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return { key: step === 'login' ? 'auth.error.badCredentials' : 'auth.error.badCode' }
    }
    if (err.status === 429) {
      return { key: 'auth.error.lockedOut' }
    }
    if (err.status === 422) return { key: 'auth.error.invalidInput' }
    return { key: 'auth.error.failed', params: { status: err.status } }
  }
  // Not an ApiError means fetch itself rejected: offline, DNS, TLS, CORS.
  return { key: 'auth.error.unreachable' }
}
